// src/AppRoutes.jsx — atualizado na Fase 1
// BrandSplash substitui LesvitaApp como fallback do Suspense no nativo.

import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { useTheme } from "./theme/ThemeContext";

// Eager imports
import Conta from "./components/auth/Conta";
import Loading from "./components/layout/Loading";
import BrandSplash from "./theme/BrandSplash";
import Giroscopio from "./components/camera/Giroscopio";
import TransferCSV from "./components/dataset/TransferCSV";
import LandingLesvita from "./landing/LandingLesvita";
import LesvitaApp from "./landing/LesvitaApp";
import Suporte from "./landing/Suporte";
import ReferralLanding from "./landing/ReferralLanding";
import PagePoliticas from "./pages/politicas/PagePoliticas";
import ExcluirConta from "./pages/politicas/ExcluirConta";
import FormsObjetivo from "./pages/FormsObjetivo";
import BemVindo from "./pages/BemVindo.jsx";
import CodigoCriadorPagina from "./features/criador/CodigoCriadorPagina.jsx";
import MonetizacaoHost from "./features/monetizacao/MonetizacaoHost";

// Prévia das telas de criador só em `npm start` (revisão visual). Em build de
// produção a condição é falsa e o import some do bundle.
const PreviaCriador = process.env.NODE_ENV === "development"
  ? React.lazy(() => import("./features/criador/PreviaCriador.jsx"))
  : null;
// Mesma ideia para os componentes da Análise Corporal (vitrine de revisão).
const VitrineAnaliseCorporal = process.env.NODE_ENV === "development"
  ? React.lazy(() => import("./features/analiseCorporal/dev/VitrineAC.jsx"))
  : null;
import useDeepLinks from "./features/vita/deeplink/useDeepLinks";
import PerfilDeepLinkBridge from "./features/social/PerfilDeepLinkBridge.jsx";

// Lazy imports
const PlataformaPessoal   = React.lazy(() => import("./pages/PlataformaPessoal"));
// Deep link contextual da VITA (WhatsApp -> app). Eager seria carregar o
// roteador de handoff em toda sessao; lazy paga so quando o link e clicado.
const DeepLinkRoute       = React.lazy(() => import("./features/vita/deeplink/DeepLinkRoute"));
// Convite de clube (/c/CODIGO). Mesma razao do lazy acima: a rota so existe
// para quem chegou por um link, e ela e quatro linhas de redirecionamento.
const ConviteClube        = React.lazy(() => import("./features/clubes/ConviteClube"));
// const Dataset             = React.lazy(() => import("./components/dataset/Dataset"));
// const DatasetVoluntar     = React.lazy(() => import("./components/dataset/DatasetVoluntar"));
// const SalaConsulta        = React.lazy(() => import("./features/agendamento/SalaConsulta"));
const PhotoNutrition      = React.lazy(() => import("./features/nutricao/PhotoNutrition"));
// Ferramenta interna de curadoria do catalogo de receitas. Lazy porque so quem
// cura o catalogo abre — ninguem mais deve pagar o bundle dela.
const ScanResultRoute     = React.lazy(() => import("./features/nutricao/ScanResultRoute"));
const CheckoutForm        = React.lazy(() => import("./components/stripe/CheckoutForm"));
// Fluxo de assinatura web (lançamento) — lazy: só quem entra no funil paga o custo.
const AssinarFluxo        = React.lazy(() => import("./landing/funil/AssinarFluxo"));
const AssinarAcesso       = React.lazy(() => import("./landing/funil/AssinarAcesso"));
// Login por CPF na web. Lazy: so quem volta pela porta da frente paga o custo.
const Entrar              = React.lazy(() => import("./landing/funil/Entrar"));
const BaixarApp           = React.lazy(() => import("./landing/funil/BaixarApp"));
const ContaAtiva          = React.lazy(() => import("./landing/funil/ContaAtiva"));
const Redirect            = React.lazy(() => import("./components/stripe/Redirect"));
const Return              = React.lazy(() => import("./components/stripe/Return"));

// ── Safe-area shell (C1) ─────────────────────────────────────────────────────
// Aplica o inset de topo (var(--safe-top)) UMA vez ao redor das rotas que fluem
// normalmente. Rotas full-bleed (câmera) e rotas height:100vh rígidas tratam o
// inset no próprio container → entram nesta lista e NÃO recebem o padding aqui
// (evita padding duplicado).
const NO_TOP_INSET_ROUTES = new Set([
  "/",            // LesvitaApp (nativo): .cabecalho trata o inset / landing web não precisa
  "/plataforma/pessoal", // AppShell é 100dvh rígido e cada tela reserva o notch
                         // no próprio cabeçalho (TopBar usa --lv-topbar-min =
                         // max(safe-top, 54)). Com o padding daqui, o shell
                         // começaria em safe-top e a TabBar cairia fora por baixo.
  "/login",       // Conta: .containerMain é height:100vh rígido (trata no próprio container)
  "/giroscopio",  // full-bleed (sensor/câmera)
  "/photo-nutri", // full-bleed (câmera)
  "/codigo-criador", // etapa do código de criador: tela cheia que trata o notch
]);

const SafeAreaShell = ({ children }) => {
  const { pathname } = useLocation();
  const inset = !NO_TOP_INSET_ROUTES.has(pathname);
  return (
    <div className={`lvSafeShell${inset ? " lvSafeShell--inset" : ""}`}>
      {children}
    </div>
  );
};

/**
 * Escuta deep links do app nativo. Precisa viver DENTRO do <Router> (usa
 * useNavigate) e fora de <Routes>, para valer em qualquer rota.
 */
/**
 * Análise Corporal por URL. A análise mora DENTRO do shell (sub-telas `ac:*`,
 * ver features/analiseCorporal/AnaliseCorporalSubtela), então estas URLs só
 * levam para /plataforma/pessoal com `?aba=avaliacao[&analiseId=…]`, que o
 * shell lê e abre a tela certa. /scanbody é o nome antigo e continua valendo.
 */
const RedirecionaAnaliseCorporal = () => {
  const { id } = useParams();
  const query = new URLSearchParams({ aba: "avaliacao", ...(id ? { analiseId: id } : null) });
  return <Navigate to={`/plataforma/pessoal?${query.toString()}`} replace />;
};

const DeepLinkListener = () => {
  useDeepLinks();
  return null;
};

const RouteOverflowController = () => {
  const location = useLocation();

  useEffect(() => {
    // Rotas que SCROLLAM. O global é `html,body,#root { overflow:hidden }`
    // (o app é uma casca de abas que rolam por dentro), então toda página de
    // documento longo precisa entrar aqui — sem isso o conteúdo é cortado e
    // não há como chegar no botão do fim. Foi o que aconteceu com o funil
    // /assinar: o formulário de pagamento não rolava.
    const isResultRoute =
      location.pathname.startsWith("/result") ||
      location.pathname.startsWith("/assinar") ||
      location.pathname.startsWith("/baixar") ||
      location.pathname.startsWith("/entrar") ||
      location.pathname.startsWith("/conta-ativa") ||
      location.pathname === "/";

    const els = [
      document.body,
      document.documentElement,
      document.getElementById("root"),
    ].filter(Boolean);

    els.forEach((el) => el.classList.toggle("overflow-auto-route", isResultRoute));
    return () => els.forEach((el) => el.classList.remove("overflow-auto-route"));
  }, [location.pathname]);

  return null;
};

// Fallback do Suspense:
// - Nativo: BrandSplash com o tema da academia (ou LesVita se não tiver)
// - Web: spinner Loading simples
const SuspenseFallback = () => {
  const isNative = Capacitor.isNativePlatform();
  const { theme, isThemeReady } = useTheme();

  if (isNative) {
    return <BrandSplash theme={theme} isReady={isThemeReady} />;
  }
  return <Loading />;
};

const useStandalone = () => {
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const iosStandalone = window.navigator.standalone === true;
    const mq = window.matchMedia("(display-mode: standalone)");
    setIsStandalone(iosStandalone || mq.matches);

    const handler = (e) => setIsStandalone(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  return isStandalone;
};

const AppRoutes = () => {
  const isNative = Capacitor.isNativePlatform();
  useStandalone();

  return (
    <Router>
      <RouteOverflowController />
      <DeepLinkListener />
      <PerfilDeepLinkBridge />
      <SafeAreaShell>
      <Suspense fallback={<SuspenseFallback />}>
        <Routes>
          <Route path="/"                   element={isNative ? <LesvitaApp /> : <LandingLesvita />} />
          <Route path="/assinar"            element={<AssinarFluxo />} />
          <Route path="/assinar/acesso"     element={<AssinarAcesso />} />
          <Route path="/baixar"             element={<BaixarApp />} />
          <Route path="/entrar"             element={<Entrar />} />
          <Route path="/conta-ativa"        element={<ContaAtiva />} />
          <Route path="/login"              element={<Conta />} />
          <Route path="/objetivo"           element={<FormsObjetivo />} />
          <Route path="/plataforma/pessoal" element={<PlataformaPessoal />} />
          <Route path="/giroscopio"         element={<Giroscopio />} />
          <Route path="/StepColeta"         element={<RedirecionaAnaliseCorporal />} />
          {/* <Route path="/dataset"            element={<Dataset />} />*/}
          {/* <Route path="/dataset-voluntar"   element={<DatasetVoluntar />} /> */}
          <Route path="/transferCSV"        element={<TransferCSV />} />
          {/* <Route path="/SalaConsulta"       element={<SalaConsulta />} /> */}
          <Route path="/photo-nutri"        element={<PhotoNutrition />} />
          {/* /uploadreceita e /uploadtreino SAIRAM daqui em 16/09/2026: viraram a
              aba Acervo de painel.lesvita.com. Eram ferramenta de operacao no
              bundle que todo usuario baixa, protegidas por uma URL que ninguem
              conhecia — e URL que ninguem conhece nao e permissao. A publicacao
              continua passando pelas mesmas callables (importRecipesCatalog e
              importWorkoutsCatalog), agora chamadas pela admin-api. */}
          <Route path="/result"             element={<ScanResultRoute />} />
          <Route path="/checkout"           element={<CheckoutForm />} />
          <Route path="/redirect"           element={<Redirect />} />
          <Route path="/return"             element={<Return />} />
          <Route path="/politicas"          element={<PagePoliticas standalone />} />
          <Route path="/excluir-conta"      element={<ExcluirConta />} />
          <Route path="/suporte"            element={<Suporte />} />
          {/* Deep links contextuais da VITA. Precisa vir ANTES do curinga
              /:referralCode, senao "/app/treino" seria lido como codigo de
              indicacao. */}
          <Route path="/app/:destino"      element={<DeepLinkRoute />} />
          {/* Análise Corporal (e o nome antigo /scanbody): antes do curinga. */}
          <Route path="/analise-corporal"     element={<RedirecionaAnaliseCorporal />} />
          <Route path="/analise-corporal/:id" element={<RedirecionaAnaliseCorporal />} />
          <Route path="/scanbody"             element={<RedirecionaAnaliseCorporal />} />
          {/* Convite de clube. Pela MESMA razao do /app/:destino acima: sem
              estar antes do curinga, "/c/ABC12345" seria lido como codigo de
              indicacao e a pessoa cairia numa landing de academia. */}
          <Route path="/c/:codigo"         element={<ConviteClube />} />
          {/* Etapa "Tem um código de criador?" do cadastro — antes do curinga. */}
          <Route path="/codigo-criador"    element={<CodigoCriadorPagina />} />
          {PreviaCriador && <Route path="/dev/criador" element={<PreviaCriador />} />}
          {VitrineAnaliseCorporal && <Route path="/dev/analise-corporal" element={<VitrineAnaliseCorporal />} />}
          <Route path="/ref/:referralCode" element={<ReferralLanding />} />
          <Route path="/:referralCode"     element={<ReferralLanding />} />
          <Route path="/bem-vindo" element={<BemVindo />} />

        </Routes>
      </Suspense>
      </SafeAreaShell>
      {/* Funil do Premium + anúncios: global (a tela de resultado do ScanFood é
          rota própria), mas DENTRO do Router — a tela de planos usa
          useNavigate, e fora dele o hook lança ao montar. Fica também sob o
          ErrorBoundary do App, em vez de derrubar a árvore inteira. */}
      <MonetizacaoHost />
    </Router>
  );
};

export default AppRoutes;
